import {
    MANDATE_APPLICANT_DESIGNATION,
    MANDATE_APPLICANT_DESIGNATION_LABELS,
    MANDATE_APPLICANT_DUPLICATE_PERSON_ERROR,
    MANDATE_APPLICANT_FIELD_KEYS,
    MANDATE_APPLICANT_PHONE_DIGIT_LENGTH,
    MANDATE_APPLICANT_PRIMARY_DUPLICATE_ERROR,
    MANDATE_APPLICANT_PRIMARY_MISSING_ERROR,
    MANDATE_APPLICANT_TYPE_KEY,
    PINCODE_DIGIT_LENGTH,
} from '../../MandateFormConstants';
import { validatePAN } from '../../../../utils/validatePAN';
import { emailRegex, isValidPincode } from '../../../../utils/utils';

// How a row's directorId was obtained. A row with no directorId was always typed manually.
export const APPLICANT_LINK_SOURCE = {
    MANUAL: 'MANUAL',
    DROPDOWN: 'DROPDOWN',
    SAVED: 'SAVED',
} as const;
export type ApplicantLinkSource = (typeof APPLICANT_LINK_SOURCE)[keyof typeof APPLICANT_LINK_SOURCE];

export const APPLICANT_EDIT_STATE = {
    MANUAL_NEW: 'MANUAL_NEW',
    DROPDOWN_SELECTED: 'DROPDOWN_SELECTED',
    RETURNING_EDIT: 'RETURNING_EDIT',
} as const;
export type ApplicantEditState = (typeof APPLICANT_EDIT_STATE)[keyof typeof APPLICANT_EDIT_STATE];

export interface ApplicantDirectorLink {
    // The existing-director profile's stable id - always present once a row is linked, unlike
    // directorId below (the bureau stamp, which a profile may not have yet).
    managementProfileId?: string | null;
    directorId?: string | null;
    linkSource?: ApplicantLinkSource;
}

// A row with no managementProfileId was always typed manually (manual-new, everything editable).
// A row that carries one is either a fresh pick from the "Select Applicants" picker this session
// (dropdown-selected: Name/PAN read-only, Phone editable - the picker may only have a masked
// phone on file, see spawnApplicantCard) or a director already linked to THIS mandate from a
// prior save (returning-edit: Name/PAN read-only, Phone editable as an intentional shared-contact
// update) - a managementProfileId with no recorded linkSource can only be a row rehydrated from a
// saved mandate, so it is treated as returning-edit too. Gated on managementProfileId rather than
// directorId because a linkable profile may have no bureau directorId yet.
export const resolveApplicantEditState = (link: ApplicantDirectorLink): ApplicantEditState => {
    if (!link?.managementProfileId) return APPLICANT_EDIT_STATE.MANUAL_NEW;
    if (link.linkSource === APPLICANT_LINK_SOURCE.DROPDOWN) return APPLICANT_EDIT_STATE.DROPDOWN_SELECTED;
    return APPLICANT_EDIT_STATE.RETURNING_EDIT;
};

// The stable link identity for an applicant rehydrated from a saved mandate. managementProfileId
// is the identity the Deal detail now round-trips; directorId is a legacy fallback for rows saved
// before managementProfileId was persisted. A truthy result feeds resolveApplicantEditState as a
// RETURNING_EDIT link, keeping Name/PAN locked on reopen even when the bureau directorId is absent.
export const resolveSavedApplicantLinkId = (
    saved: { managementProfileId?: string | null; directorId?: string | null } | null | undefined,
): string | undefined => saved?.managementProfileId || saved?.directorId || undefined;

const normalizeAddress = (value: any): string => (typeof value === 'string' ? value.trim() : '');

// The toggle is never persisted on its own (see PRESENCE_FIELD_KEYS below and buildApplicantDetail
// in useManageDealDrawer, which mirrors permanentAddress from currentAddress only when the toggle
// was on at save time) - so on reopen it must be RE-DERIVED from the saved addresses, or a user who
// edits Current Address without re-ticking the toggle gets the two addresses silently diverging.
// Two blanks (or only one side present) never derive true - equality of nothing isn't "same as".
export const resolveSavedPermanentSameAsCurrent = (saved: Record<string, any> | null | undefined): boolean => {
    const current = normalizeAddress(saved?.[MANDATE_APPLICANT_FIELD_KEYS.CURRENT_ADDRESS]);
    const permanent = normalizeAddress(saved?.[MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_ADDRESS]);
    return !!current && current === permanent;
};

export const READ_ONLY_FIELD_KEYS_BY_EDIT_STATE: Record<ApplicantEditState, string[]> = {
    [APPLICANT_EDIT_STATE.MANUAL_NEW]: [],
    [APPLICANT_EDIT_STATE.DROPDOWN_SELECTED]: [MANDATE_APPLICANT_FIELD_KEYS.NAME, MANDATE_APPLICANT_FIELD_KEYS.PAN],
    [APPLICANT_EDIT_STATE.RETURNING_EDIT]: [MANDATE_APPLICANT_FIELD_KEYS.NAME, MANDATE_APPLICANT_FIELD_KEYS.PAN],
};

const isFieldFilled = (value: any): boolean => (typeof value === 'string' ? !!value.trim() : !!value);

// Trims + uppercases a typed/prefilled PAN before format-checking, deduping, or persisting it -
// a lowercase or padded PAN must behave identically to its canonical form everywhere. Non-string
// input passes through unchanged.
const normalizePan = (value: any): any => (typeof value === 'string' ? value.trim().toUpperCase() : value);

// Every field that can make a row/the primary applicant "present", EXCLUDING
// PERMANENT_SAME_AS_CURRENT - that field is a UI-only toggle (a "Yes" pick stores boolean `true`),
// not an identifying/detail value, so it must never by itself make an otherwise-blank card count
// as present.
const PRESENCE_FIELD_KEYS = Object.values(MANDATE_APPLICANT_FIELD_KEYS).filter(
    (key) => key !== MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_SAME_AS_CURRENT,
);

// A row/the primary applicant counts as "present" once ANY identifying or detail field has a
// value - an in-progress row must block Proceed (see isApplicantEntryValid), never get silently
// dropped from the save payload for lacking a name. Shared by the people[] assembler and the
// Proceed gate (useManageDealDrawer) so both agree on which rows are real applicants.
export const isApplicantEntryPresent = (fields: Record<string, any>): boolean =>
    PRESENCE_FIELD_KEYS.some((key) => isFieldFilled(fields?.[key]));

// Phone/email are optional (UW's Joi schema allows null/blank for both) - only a PRESENT-but-
// malformed value is invalid. Shared with ApplicantFieldsBlock's inline field-level red error, so
// the Proceed gate and the visible error always agree.
export const isApplicantPhoneValid = (value: any): boolean => !value || value.length === MANDATE_APPLICANT_PHONE_DIGIT_LENGTH;
export const isApplicantEmailValid = (value: any): boolean => !value || emailRegex.test(value);
export const isApplicantPincodeValid = (value: any): boolean => isValidPincode(value, PINCODE_DIGIT_LENGTH);

// A present row is only valid to save once it has a name, a valid PAN, and (when present) a
// well-formed phone/email/pincode - the fields the backend requires or hard-rejects the whole
// batch on.
export const isApplicantEntryValid = (fields: Record<string, any>): boolean =>
    isFieldFilled(fields?.[MANDATE_APPLICANT_FIELD_KEYS.NAME]) &&
    validatePAN(normalizePan(fields?.[MANDATE_APPLICANT_FIELD_KEYS.PAN])) &&
    isApplicantPhoneValid(fields?.[MANDATE_APPLICANT_FIELD_KEYS.PHONE]) &&
    isApplicantEmailValid(fields?.[MANDATE_APPLICANT_FIELD_KEYS.EMAIL]) &&
    isApplicantPincodeValid(fields?.[MANDATE_APPLICANT_FIELD_KEYS.CURRENT_ADDRESS_PINCODE]);

export interface ExistingDirectorOption {
    // Always present (unlike directorId, the bureau stamp) - the "Select Applicants" picker keys
    // its options on this.
    managementProfileId: string;
    directorId?: string | null;
    name: string;
    pan?: string;
    phone?: string;
    email?: string;
}

// Cross-file data type: rendered by CoApplicantSection, but the row state itself is owned and
// mutated by useManageDealDrawer.
export interface CoApplicantRow extends ApplicantDirectorLink {
    id: string;
    persisted: boolean;
    [fieldKey: string]: any;
}

// A linked existing-director's phone prefilled from getManagementProfiles arrives PII-masked
// (e.g. "*******006") - a masked value is a display-only artifact, so it's stripped (UW already
// holds the real phone on the linked ManagementProfile) - never try to unmask. Blank strips too
// (never overwrite the real phone with empty). A genuinely user-typed value passes through
// UNCHANGED even when invalid/partial - silently dropping it would make the phone vanish with no
// error; UW's own validation is what surfaces a specific error for a bad value.
export const stripMaskedPhone = (value: any): string | undefined => {
    if (typeof value !== 'string' || !value.trim()) return undefined;
    return value.includes('*') ? undefined : value;
};

// The fixed `people[]` shape the godzilla ServiceDefinition body expects - `clientRef` is what
// the save response joins its per-person result back to. managementProfileId (the linked
// profile's stable `_id`, when the card was picked/rehydrated rather than typed manually) is what
// lets UW link that exact row instead of guessing via its active-only name/PAN dedup - the bureau
// directorId is never a link selector and is not sent here.
export const buildPersonEntry = (
    fields: Record<string, any>,
    clientRef: string,
    managementProfileId: string | null | undefined,
    // Sent ONLY when creating a brand-new person - for a linked/existing director we omit it so UW
    // leaves their real (bureau/KARZA) designation untouched; the per-mandate Primary/Co role lives
    // on the Deal bucket, not on the shared managementProfiles row.
    designation?: string,
) => ({
    clientRef,
    ...(designation ? { designation } : {}),
    name: fields[MANDATE_APPLICANT_FIELD_KEYS.NAME],
    primaryPan: normalizePan(fields[MANDATE_APPLICANT_FIELD_KEYS.PAN]),
    phoneNumber: stripMaskedPhone(fields[MANDATE_APPLICANT_FIELD_KEYS.PHONE]),
    email: fields[MANDATE_APPLICANT_FIELD_KEYS.EMAIL],
    dob: fields[MANDATE_APPLICANT_FIELD_KEYS.DOB],
    ...(managementProfileId ? { managementProfileId } : {}),
});

export interface ApplicantCardLink {
    directorId?: string | null;
    managementProfileId?: string | null;
}

// A card + the physical link data it saves against. clientRef stays tied to the physical slot
// ('primary' for the primary slot, the row id for a co row) regardless of which bucket the card's
// applicantType field puts it in - the save-response join depends on it (see coApplicantTypes'
// callers in useManageDealDrawer).
export interface ApplicantCard {
    clientRef: string;
    fields: Record<string, any>;
    link: ApplicantCardLink;
}

// Every PRESENT card (primary slot + co-applicant rows), in physical order.
const buildPresentApplicantCards = (
    primaryFields: Record<string, any>,
    primaryLink: ApplicantCardLink,
    coApplicants: CoApplicantRow[],
): ApplicantCard[] => [
    ...(isApplicantEntryPresent(primaryFields) ? [{ clientRef: 'primary', fields: primaryFields, link: primaryLink }] : []),
    ...coApplicants
        .filter((row) => isApplicantEntryPresent(row))
        .map((row) => ({
            clientRef: row.id,
            fields: row,
            link: { directorId: row.directorId, managementProfileId: row.managementProfileId },
        })),
];

// Whether a card's applicantType field marks it the Primary Applicant - the field decides the
// primary/co-applicant bucket, never the card's physical slot.
const isMarkedPrimary = (card: ApplicantCard): boolean => card.fields[MANDATE_APPLICANT_TYPE_KEY] === MANDATE_APPLICANT_DESIGNATION_LABELS.PRIMARY;

// Present-card count + how many of them are marked Primary Applicant - shared by the Proceed gate,
// the inline one-Primary banner, and the pre-save belt-and-braces check, so all three agree on the
// same numbers.
export const getApplicantPresenceCounts = (
    primaryFields: Record<string, any>,
    coApplicants: CoApplicantRow[],
): { totalPresentCount: number; primaryMarkedCount: number } => {
    const cards = buildPresentApplicantCards(primaryFields, {}, coApplicants);
    return { totalPresentCount: cards.length, primaryMarkedCount: cards.filter(isMarkedPrimary).length };
};

// Which one-Primary validation error applies (or '' when the roster is valid) - a present roster
// requires exactly one Primary; an empty roster requires none (A-40: BD-only saves are unaffected).
export const resolveApplicantPrimaryTypeError = (totalPresentCount: number, primaryMarkedCount: number): string => {
    if (totalPresentCount === 0 || primaryMarkedCount === 1) return '';
    return primaryMarkedCount === 0 ? MANDATE_APPLICANT_PRIMARY_MISSING_ERROR : MANDATE_APPLICANT_PRIMARY_DUPLICATE_ERROR;
};

// Two present applicant cards may not be the same person: reject when two cards share the same
// well-formed PAN (a picked card prefills fields.PAN from the director - spawnApplicantCard - so
// this also catches a typed PAN matching a picked director's PAN) or the same picked
// managementProfileId. Returns '' when the roster is unique. Best-effort fast pre-check; the
// backend's directorId-collision guard is the authoritative catch-all (e.g. a masked PAN or a
// name-only match, neither of which this helper can see).
export const findDuplicateApplicant = (
    primaryFields: Record<string, any>,
    primaryLink: ApplicantCardLink,
    coApplicants: CoApplicantRow[],
): string => {
    const cards = buildPresentApplicantCards(primaryFields, primaryLink, coApplicants);
    const seenPans = new Set<string>();
    const seenIds = new Set<string>();
    for (const card of cards) {
        const rawPan = card.fields[MANDATE_APPLICANT_FIELD_KEYS.PAN];
        const pan = typeof rawPan === 'string' ? normalizePan(rawPan) : '';
        // Only compare FULL, well-formed PANs - a masked/partial value (e.g. picker PII mask) must
        // never equality-match, or two distinct people could false-positive.
        if (validatePAN(pan)) {
            if (seenPans.has(pan)) return MANDATE_APPLICANT_DUPLICATE_PERSON_ERROR;
            seenPans.add(pan);
        }
        const id = card.link.managementProfileId;
        if (id) {
            if (seenIds.has(id)) return MANDATE_APPLICANT_DUPLICATE_PERSON_ERROR;
            seenIds.add(id);
        }
    }
    return '';
};

// Splits every present card into the one its applicantType field marks Primary Applicant and the
// rest - used to build the primaryApplicant/coApplicants[] save payload split. Only call once the
// roster has been validated to have exactly one Primary-marked card (see
// resolveApplicantPrimaryTypeError) - otherwise the first Primary-marked card wins arbitrarily.
export const splitApplicantCardsByType = (
    primaryFields: Record<string, any>,
    primaryLink: ApplicantCardLink,
    coApplicants: CoApplicantRow[],
): { primaryCard?: ApplicantCard; coCards: ApplicantCard[] } => {
    const cards = buildPresentApplicantCards(primaryFields, primaryLink, coApplicants);
    const primaryCard = cards.find(isMarkedPrimary);
    return { primaryCard, coCards: cards.filter((card) => card !== primaryCard) };
};

// Assembles the save payload's `people[]` from every PRESENT card - an empty roster (no primary,
// no co-applicants) yields [], which is how the save call itself gets skipped as a valid
// save-nothing Proceed. Designation comes from each card's applicantType field, not its physical
// slot - a co-slot card marked Primary Applicant sends designation APPLICANT.
export const buildMandateApplicantPeople = (
    primaryFields: Record<string, any>,
    primaryManagementProfileId: string | null | undefined,
    coApplicants: CoApplicantRow[],
) =>
    buildPresentApplicantCards(primaryFields, { managementProfileId: primaryManagementProfileId }, coApplicants).map((card) => {
        // A linked/existing director (picked from the dropdown or a previously-saved row) carries a
        // managementProfileId/directorId - do NOT send designation for them (preserve their real
        // designation; per-mandate role is the Deal bucket). Only a brand-new person gets stamped.
        const isExistingDirector = !!(card.link.managementProfileId || card.link.directorId);
        const designation = isExistingDirector
            ? undefined
            : isMarkedPrimary(card)
            ? MANDATE_APPLICANT_DESIGNATION.APPLICANT
            : MANDATE_APPLICANT_DESIGNATION.CO_APPLICANT;
        return buildPersonEntry(card.fields, card.clientRef, card.link.managementProfileId, designation);
    });

// The save is always HTTP 200 - success/failure is carried in responseBody.errorMessage, never
// HTTP status - so Proceed must block on this field, not the response's status code. A missing/
// unexpected body is also treated as a failure, never a silent success.
export const shouldBlockOnResponse = (responseBody: Record<string, any> | undefined): boolean =>
    !responseBody || !!responseBody.errorMessage;
